import React, { useState } from 'react';
import {
  Input,
  Button,
  Row,
  Col,
  Checkbox,
  Typography,
  Space,
  Modal,
} from 'antd';
import {
  DeleteOutlined,
  CopyOutlined,
  EditOutlined,
  PlusOutlined,
  SaveOutlined,
} from '@ant-design/icons';

import styled from 'styled-components';

const { Title, Text, Link } = Typography;

const LargeCheckbox = styled(Checkbox)`
  .ant-checkbox-inner {
    width: 24px;
    height: 24px;
    border-radius: 6px;
  }
  .ant-checkbox-inner:after {
    left: 30%;
    top: 45%;
    width: 8px;
    height: 14px;
    border-width: 3px;
  }
  .ant-checkbox {
    font-size: 28px;
  }
`;

const Section = ({ section, onDelete, onDuplicate, onChange, editable }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingLabel, setEditingLabel] = useState(false);
  const [tempLabel, setTempLabel] = useState(
    section.freeTextLabel || 'Notes: '
  );

  const checkBoxIcons = ['✔️', '⚠️', '❌', '🚫'];

  const checkBoxPlaceholders = ['Pass', 'Caution', 'Fail', 'N/A'];

  const [localLabels, setLocalLabels] = useState(
    section.checkboxLabels &&
      section.checkboxLabels.length === section.checkboxes.length
      ? [...section.checkboxLabels]
      : section.checkboxes.map(
          (_, idx) => checkBoxPlaceholders[idx] || `Checkbox ${idx + 1}`
        )
  );

  const [localImportant, setLocalImportant] = useState(
    section.checkboxImportant &&
      section.checkboxImportant.length === section.checkboxes.length
      ? [...section.checkboxImportant]
      : section.checkboxes.map(() => false)
  );

  const openEditModal = () => {
    setLocalLabels(
      section.checkboxLabels &&
        section.checkboxLabels.length === section.checkboxes.length
        ? [...section.checkboxLabels]
        : section.checkboxes.map(
            (_, idx) => checkBoxPlaceholders[idx] || `Checkbox ${idx + 1}`
          )
    );
    setLocalImportant(
      section.checkboxImportant &&
        section.checkboxImportant.length === section.checkboxes.length
        ? [...section.checkboxImportant]
        : section.checkboxes.map(() => false)
    );
    setModalOpen(true);
  };

  const handleSaveLabels = () => {
    onChange('checkboxLabelsAndImportant', null, {
      labels: localLabels,
      important: localImportant,
    });
    setModalOpen(false);
  };

  const getCheckboxLabel = (idx) => {
    let label =
      section.checkboxLabels &&
      section.checkboxLabels[idx] &&
      section.checkboxLabels[idx].trim() !== ''
        ? section.checkboxLabels[idx]
        : checkBoxPlaceholders[idx] || `Checkbox ${idx + 1}`;
    return `${checkBoxIcons[idx]} ${label}`;
  };

  const saveLabel = () => {
    onChange('freeTextLabel', null, tempLabel);
    setEditingLabel(false);
  };

  return (
    <div
      style={{
        marginBottom: 8,
        padding: 12,
        background: '#fff',
        borderRadius: 8,
        border: '1px solid #f0f0f0',
        position: 'relative',
      }}
    >
      <Row justify="end" align="middle" style={{ marginBottom: 8 }}>
        <Space size={2}>
          <Button
            icon={<DeleteOutlined />}
            danger
            type="text"
            onClick={onDelete}
            style={{ fontSize: 14, paddingLeft: 8, paddingRight: 0 }}
          >
            Delete section
          </Button>
          <Button
            icon={<CopyOutlined />}
            type="text"
            onClick={onDuplicate}
            style={{
              color: '#1677ff',
              fontSize: 14,
              paddingLeft: 8,
              paddingRight: 8,
            }}
          >
            Duplicate section
          </Button>
        </Space>
      </Row>
      <Row
        gutter={25}
        align="middle"
        style={{ marginBottom: 8 }}
        className="checkbox-row"
      >
        {section.checkboxes.map((checked, idx) => (
          <Col
            span={5}
            key={idx}
            style={{
              textAlign: 'center',
              minWidth: 60,
              padding: 0,
              wordBreak: 'break-word',
            }}
          >
            <Text>{getCheckboxLabel(idx)}</Text>
            <div>
              <LargeCheckbox
                checked={checked}
                disabled
                style={{
                  width: 24,
                  height: 24,
                  marginTop: 8,
                  marginBottom: 8,
                }}
                onChange={(e) => onChange('checkboxes', idx, e.target.checked)}
              />
            </div>
          </Col>
        ))}
        <Col
          span={4}
          style={{ textAlign: 'right', marginTop: 8, paddingLeft: -12 }}
        >
          <Link onClick={openEditModal} disabled={!editable}>
            <EditOutlined /> Edit
          </Link>
        </Col>
      </Row>
      <Row gutter={24} align="middle" style={{ marginBottom: 8 }}>
        <Col span={12}>
          <Text>Description:</Text>
          <Input
            value={section.description}
            placeholder="Enter description text here"
            style={{ marginTop: 4 }}
            suffix={<EditOutlined />}
            onChange={(e) => onChange('description', null, e.target.value)}
            disabled={!editable}
          />
        </Col>
        <Col span={12}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {editingLabel ? (
              <>
                <Input
                  value={tempLabel}
                  onChange={(e) => setTempLabel(e.target.value)}
                  onPressEnter={saveLabel}
                  onBlur={saveLabel}
                  size="small"
                  style={{ maxWidth: 180 }}
                  autoFocus
                  maxLength={30}
                  disabled={!editable}
                />
              </>
            ) : (
              <Text>
                {section.freeTextLabel || 'Notes: '}
                {editable && (
                  <EditOutlined
                    style={{ fontSize: 14, cursor: 'pointer', marginLeft: 6 }}
                    onClick={() => {
                      setTempLabel(section.freeTextLabel || 'Notes: ');
                      setEditingLabel(true);
                    }}
                  />
                )}
              </Text>
            )}
          </div>
          <Input
            value={section.freeText}
            placeholder=""
            disabled
            style={{ marginTop: 4 }}
            onChange={(e) => onChange('freeText', null, e.target.value)}
          />
        </Col>
      </Row>

      <Modal
        title="Checkbox set up"
        open={modalOpen}
        onOk={handleSaveLabels}
        onCancel={() => setModalOpen(false)}
        okText="Save"
        cancelText="Cancel"
        destroyOnClose
      >
        <Row align="middle" style={{ marginBottom: 8 }}>
          <Col span={24}>
            {/* <Col span={17}> */}
            <Text>Label for checkbox</Text>
          </Col>
          {/* <Col span={7} style={{ textAlign: 'center' }}>
            <Text>Add to work order if checked</Text>
          </Col> */}
        </Row>
        {localLabels.map((label, idx) => (
          <Row key={idx} align="middle" style={{ marginBottom: 8 }}>
            <Col span={24}>
              {/* <Col span={17}> */}
              <Input
                key={idx}
                value={label}
                onChange={(e) => {
                  const newLabels = [...localLabels];
                  newLabels[idx] = e.target.value;
                  setLocalLabels(newLabels);
                }}
                style={{ marginBottom: 8 }}
                placeholder={checkBoxPlaceholders[idx] || `Checkbox ${idx + 1}`}
              />
            </Col>
            {/* <Col span={7} style={{ textAlign: 'center' }}>
              <Checkbox
                checked={localImportant[idx]}
                onChange={(e) => {
                  const newImportant = [...localImportant];
                  newImportant[idx] = e.target.checked;
                  setLocalImportant(newImportant);
                }}
                style={{ marginLeft: 12 }}
              />
            </Col> */}
          </Row>
        ))}
      </Modal>
    </div>
  );
};

export default Section;
